import { ProductWithCalculations } from '../shared/types/inventory';
import { useSettings } from '../shared/contexts/SettingsContext';
import { ProductHeader } from './ProductHeader';
import { AquariumTank } from './AquariumTank';
import { StockMarkers } from './StockMarkers';
import { InfoCallout } from './InfoCallout';
import { MetricsPanel } from './MetricsPanel';
import { LegendPanel } from './LegendPanel';

interface ProductDetailModalProps {
  product: ProductWithCalculations;
  onClose: () => void;
}

export const ProductDetailModal = ({ product, onClose }: ProductDetailModalProps) => {
  //const { settings } = useSettings();
  const settings = {diasCoberturaAlvo:21, leadTime:7}
  const currentStockPercentage = Math.max(0, Math.min(100, (product.estoqueAtual / product.estoqueAlvo) * 100));
  const ropPercentage = Math.max(0, Math.min(100, (product.rop / product.estoqueAlvo) * 100));
  const ssPercentage = Math.max(0, Math.min(100, (product.ss / product.estoqueAlvo) * 100));

  const getWaterColor = () => {
    switch (product.status) {
      case 'ruptura':
      case 'iminente':
        return '#dc2626';
      case 'critica':
        return '#f59e0b';
      case 'minimo':
        return '#eab308';
      case 'excesso':
        return '#06b6d4';
      default:
        return '#3b82f6';
    }
  };

  const ropY = 80 + (340 * (100 - ropPercentage) / 100);
  const ssY = 335;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-7xl w-full max-h-[95vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <ProductHeader
          name={product.nome}
          ean={product.ean}
          status={product.status}
          statusLabel={product.statusLabel}
          onClose={onClose}
        />

        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 border border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900 mb-6 text-center">
                  Visualização de Estoque
                </h3>

                <AquariumTank
                  waterHeight={currentStockPercentage}
                  waterColor={getWaterColor()}
                >
                  <StockMarkers
                    ropPercentage={ropPercentage}
                    targetStockValue={product.estoqueAlvo}
                    ropValue={product.rop}
                  />

                  <InfoCallout
                    position="left"
                    x={100}
                    y={80}
                    title="Estoque Alvo"
                    value={`${product.estoqueAlvo.toFixed(0)} un`}
                    description={`${settings.diasCoberturaAlvo} dias de cobertura`}
                    color="#10b981"
                  />

                  <InfoCallout
                    position="left"
                    x={100}
                    y={ropY}
                    title="ROP"
                    value={`${product.rop.toFixed(0)} un`}
                    description="Ponto de Pedido"
                    color="#f59e0b"
                  />

                  <InfoCallout
                    position="right"
                    x={500}
                    y={80 + (340 * (100 - currentStockPercentage) / 100) + 40}
                    title="Estoque Atual"
                    value={`${product.estoqueAtual.toFixed(0)} un`}
                    description={`${currentStockPercentage.toFixed(1)}% do alvo`}
                    color={getWaterColor()}
                  />

                  <InfoCallout
                    position="left"
                    x={100}
                    y={ssY}
                    title="SS"
                    value={`${product.ss.toFixed(0)} un`}
                    description="Segurança"
                    color="#475569"
                  />

                  <InfoCallout
                    position="right"
                    x={500}
                    y={250}
                    title="CMD"
                    value={`${product.cmd.toFixed(2)} un/dia`}
                    description="Consumo Médio"
                    color="#3b82f6"
                  />
                </AquariumTank>
              </div>

              <LegendPanel />
            </div>

            <div className="lg:col-span-1">
              <MetricsPanel
                product={product}
                leadTime={settings.leadTime}
                targetCoverage={settings.diasCoberturaAlvo}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
